import { trackPlanStartWorkoutClick } from 'analytics/trackers/workout';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { PRIVATE } from 'router/routes';

import { createWorkout, getWorkoutById } from 'store/workouts/actions';

import Skeleton from 'components/Skeleton';

import { WorkoutPlanType } from 'types/workouts';

import { MealPlanWidget } from '../components/MealPlanWidget/MealPlanWidget';

import useGetFormattedWorkoutPlan from './useGetFormattedWorkoutPlan';
import { useMealPlanWidget } from './useMealPlanWidget';
import { useWorkoutPlanComponentMap } from './useWorkoutPlanComponentMap';

type PlanState = 'loading' | 'hidden' | 'shown';

export const usePlanContent = () => {
    const dispatch = useDispatch();
    const { plan, isLoading, selectedDate } = useGetFormattedWorkoutPlan();

    const planMap = useMemo(() => new Map(plan.map((item) => [item.hash, item])), [plan]);
    const planMapById = useMemo(() => new Map(plan.map((item) => [item.workout_id, item])), [plan]);

    const openWorkout = useCallback(
        (planItem: WorkoutPlanType) => {
            if (planItem.workout_id !== null) {
                dispatch(
                    getWorkoutById({
                        id: planItem.workout_id,
                        returnTo: PRIVATE.WORKOUT_PLAN.path,
                        planHash: planItem.hash,
                        name: planItem.name,
                    })
                );
            } else {
                dispatch(
                    createWorkout({
                        returnTo: PRIVATE.WORKOUT_PLAN.path,
                        planHash: planItem.hash,
                        params: {
                            workout_type: planItem.workout_type,
                            warm_up: planItem.warm_up,
                            cool_down: planItem.cool_down,
                            workout_time: planItem.workout_time,
                            workout_method: planItem.workout_method,
                            workout_fitness_level: planItem.fitness_level,
                            equipments: planItem.equipment_list ?? [],
                            target_areas: planItem.target_areas,
                            name: planItem.name,
                        },
                        onSuccess: () => undefined,
                        onError: () => undefined,
                    })
                );
            }
        },
        [dispatch]
    );

    const handlePlanItemClick = ({ hash, workout_id }: Pick<WorkoutPlanType, 'hash' | 'workout_id'>) => {
        const planItem = (hash && planMap.get(hash)) || (workout_id && planMapById.get(workout_id)) || undefined;

        if (!planItem) return;

        trackPlanStartWorkoutClick();
        openWorkout(planItem);
    };

    const { widgetProps, state } = useMealPlanWidget({ selectedDate });
    const planContent = useWorkoutPlanComponentMap({ plan, isLoading, onItemClick: handlePlanItemClick });

    const contentMap: Record<PlanState, React.ReactElement | null> = {
        loading: <Skeleton height={186} mb={24} />,
        hidden: planContent,
        shown: (
            <>
                <MealPlanWidget {...widgetProps} />
                {planContent}
            </>
        ),
    };

    return { content: contentMap[state] };
};
